const caretTypes = ['text', 'search', 'url', 'tel', 'password'];

export default {
  ClickToEdit: {
    mounted() {
      this.ignoreFieldsForFillers();
      this.openSelectIfVisible();
      this.keepCaretAtEnd();
      this.focusVisibleFieldAtEnd();
      this.el.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;

        // Keep a surrounding sheet or modal from closing on the same keypress.
        event.stopPropagation();
        // A dispatched `reset` event does not reset a form, so restore the values here.
        // Self-saving editors render as a div, which has nothing to reset.
        if (typeof this.el.reset === 'function') this.el.reset();
        this.liveSocket.execJS(this.el, this.el.dataset.cancel);
      });
    },

    updated() {
      this.ignoreFieldsForFillers();
      this.openSelectIfVisible();
    },

    // Opening focuses the field programmatically, which leaves the caret at the
    // start of the value. A pointer press is the user choosing a spot, so leave that.
    keepCaretAtEnd() {
      this.el.addEventListener('pointerdown', event => {
        this.pointerPlacingCaret = !!this.caretFieldFrom(event.target);
        requestAnimationFrame(() => {
          this.pointerPlacingCaret = false;
        });
      });

      this.el.addEventListener('focusin', event => {
        const field = this.caretFieldFrom(event.target);
        if (!field) return;
        if (this.pointerPlacingCaret) {
          this.pointerPlacingCaret = false;
          return;
        }
        // Tabbing in selects the whole value, which is worth keeping. The programmatic
        // focus this corrects arrives with the caret collapsed at the start instead.
        if (field.selectionStart !== field.selectionEnd) return;
        this.placeCaretAtEnd(field);
      });
    },

    focusVisibleFieldAtEnd() {
      if (this.el.classList.contains('hidden')) return;
      const field = this.caretFieldFrom(
        this.el.querySelector('input:not([type="hidden"]):not([hidden]), textarea:not([hidden])'),
      );
      if (!field) return;
      field.focus();
      this.placeCaretAtEnd(field);
    },

    caretFieldFrom(target) {
      const field = target instanceof Element ? target.closest('input, textarea') : null;
      if (!field || !this.el.contains(field) || field.hidden || field.disabled) return null;
      if (field instanceof HTMLTextAreaElement) return field;
      if (!(field instanceof HTMLInputElement)) return null;
      if (!caretTypes.includes(field.type)) return null;
      return field;
    },

    placeCaretAtEnd(field) {
      const apply = () => {
        try {
          const length = field.value.length;
          field.setSelectionRange(length, length);
        } catch {
          // email, number, and date inputs have no text caret
        }
      };

      apply();
      // WebKit moves the caret back to the start after focus returns. Correct that
      // only when it is still there, so an arrow key or typed character in between
      // is left alone. A later programmatic focus fires focusin again and schedules
      // its own correction.
      setTimeout(() => {
        if (!field.isConnected || document.activeElement !== field) return;
        if (this.pointerPlacingCaret) return;
        if (field.selectionStart !== 0 || field.selectionEnd !== 0) return;
        if (field.value.length === 0) return;
        apply();
      }, 0);
    },

    // Click-to-edit that wraps a Fluxon select should open the listbox on the same
    // click that reveals the editor, so the user is not forced through a second click.
    openSelectIfVisible() {
      if (this.el.classList.contains('hidden')) {
        this.openedSelect = false;
        return;
      }
      if (this.openedSelect) return;
      const toggle = this.el.querySelector('[data-part="toggle"]');
      if (!toggle) return;
      this.openedSelect = true;
      queueMicrotask(() => toggle.click());
    },

    // An inline editor holds a value already on screen, never a credential, so nothing in one
    // is worth offering to fill. Doing this here covers every editor, and every field added to
    // one later, and it reapplies on update because a patch drops attributes the server never
    // rendered.
    ignoreFieldsForFillers() {
      this.el.querySelectorAll('input, textarea').forEach(field => {
        field.setAttribute('autocomplete', 'off');
        field.setAttribute('data-1p-ignore', '');
      });
    },
  },
};
